function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}



class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);


// class SliderComponentCustom extends HTMLElement {
//   constructor() {
//     super();
//     this.slider = this.querySelector('[id^="Slider-"]');
//     this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
//     this.enableSliderLooping = false;
//     this.currentPageElement = this.querySelector('.slider-counter--current');
//     this.pageTotalElement = this.querySelector('.slider-counter--total');
//     this.prevButton = this.querySelector('button[name="previous"]');
    
//     this.nextButton = this.querySelector('button[name="next"]');

//     if (!this.slider || !this.nextButton) return;

//     this.initPages();
//     const resizeObserver = new ResizeObserver((entries) => this.initPages());
//     resizeObserver.observe(this.slider);

//     this.slider.addEventListener('scroll', this.update.bind(this));
//     this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
//     this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
//   }

//   initPages() {
//     this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
//     if (this.sliderItemsToShow.length < 2) return;
//     this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
//     this.slidesPerPage = Math.floor(
//       (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
//     );
//     this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
//     this.update();
//   }

//   resetPages() {
//     this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
//     this.initPages();
//   }

//   update() {
//     // Temporarily prevents unneeded updates resulting from variant changes
//     // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
//     if (!this.slider || !this.nextButton) return;

//     const previousPage = this.currentPage;
//     this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

//     if (this.currentPageElement && this.pageTotalElement) {
//       this.currentPageElement.textContent = this.currentPage;
//       this.pageTotalElement.textContent = this.totalPages;
//     }

//     if (this.currentPage != previousPage) {
//       this.dispatchEvent(
//         new CustomEvent('slideChanged', {
//           detail: {
//             currentPage: this.currentPage,
//             currentElement: this.sliderItemsToShow[this.currentPage - 1],
//           },
//         })
//       );
//     }

//     if (this.enableSliderLooping) return;

//     if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
//       this.prevButton.setAttribute('disabled', 'disabled');
//     } else {
//       this.prevButton.removeAttribute('disabled');
//     }

//     if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
//       this.nextButton.setAttribute('disabled', 'disabled');
//     } else {
//       this.nextButton.removeAttribute('disabled');
//     }
//   }

//   isSlideVisible(element, offset = 0) {
//     const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
//     return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
//   }

//   onButtonClick(event) {
//     event.preventDefault();
//     const step = event.currentTarget.dataset.step || 1;
//     this.slideScrollPosition =
//       event.currentTarget.name === 'next'
//         ? this.slider.scrollLeft + step * this.sliderItemOffset
//         : this.slider.scrollLeft - step * this.sliderItemOffset;
//     this.setSlidePosition(this.slideScrollPosition);
//   }

//   setSlidePosition(position) {
//     this.slider.scrollTo({
//       left: position,
//     });
//   }
// }

// customElements.define('custom-slider-component', SliderComponentCustom);

class CustomSliderComponent extends HTMLElement {
  constructor() {
    super();
    this._isJumping = false;
    this._isScrolling = false;
    this._scrollTimeout = null;
    this._clickTimeout = null;
    this._autoplayTimer = null;
    this._autoplayDelay = 5000;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this._stopAutoplay();
    if (this._scrollHandler) {
      this.slider?.removeEventListener('scroll', this._scrollHandler);
    }
  }

  init() {
    // Clean up old listeners and clones
    if (this._scrollHandler) {
      this.slider?.removeEventListener('scroll', this._scrollHandler);
    }
    this.querySelectorAll('.clone').forEach(el => el.remove());

    this.slider = this.querySelector('[id^="Slider-"]');
    this.realItems = Array.from(this.querySelectorAll('[id^="Slide-"]'));
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.prevButton || !this.nextButton || this.realItems.length < 2) return;

    // Detect RTL
    this.isRTL = document.documentElement.dir === 'rtl' || 
                 document.body.dir === 'rtl' ||
                 getComputedStyle(this.slider).direction === 'rtl';

    this._setupClones();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._measure();
        this._goToRealStart();

        this._resizeObserver?.disconnect();
        this._resizeObserver = new ResizeObserver(() => {
          this._measure();
          this._goToRealStart();
        });
        this._resizeObserver.observe(this.slider);
      });
    });

    this._scrollHandler = this._onScroll.bind(this);
    this.slider.addEventListener('scroll', this._scrollHandler, { passive: true });

    this.prevButton.onclick = null;
    this.nextButton.onclick = null;
    this.prevButton.addEventListener('click', this._onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this._onButtonClick.bind(this));

    this.prevButton.removeAttribute('disabled');
    this.nextButton.removeAttribute('disabled');

    this._setupAutoplay();
  }

  _setupAutoplay() {
    this._stopAutoplay();
    const enabled = this.dataset.autoplayEnabled === 'true';
    const delay = parseInt(this.dataset.autoplayDelay) || 5000;
    this._autoplayDelay = delay;

    if (!enabled || this.realItems.length < 2) return;

    this._startAutoplay();
    this.addEventListener('mouseenter', this._stopAutoplay);
    this.addEventListener('mouseleave', this._startAutoplay);
  }

  _startAutoplay = () => {
    this._stopAutoplay();
    this._autoplayTimer = setInterval(() => {
      if (!this.itemOffset) return;
      const currentScroll = this._getScrollLeft();
      const targetScroll = currentScroll + this.itemOffset;
      this._setScrollLeft(targetScroll, 'smooth');

      clearTimeout(this._clickTimeout);
      this._clickTimeout = setTimeout(() => {
        this._checkLoop();
      }, 450);
    }, this._autoplayDelay);
  };

  _stopAutoplay = () => {
    clearInterval(this._autoplayTimer);
    this._autoplayTimer = null;
  };

  _setupClones() {
    const items = this.realItems;

    // Append clones after real items (trailing)
    items.forEach(item => {
      const clone = item.cloneNode(true);
      clone.classList.add('clone');
      clone.removeAttribute('id');
      this.slider.appendChild(clone);
    });

    // Prepend clones before real items (leading)
    [...items].reverse().forEach(item => {
      const clone = item.cloneNode(true);
      clone.classList.add('clone');
      clone.removeAttribute('id');
      this.slider.prepend(clone);
    });
  }

  _measure() {
    const allSlides = Array.from(this.slider.children).filter(el => el.clientWidth > 0);
    if (allSlides.length < 2) return;

    // Use absolute offsetLeft difference for item width
    const offsets = allSlides.map(el => el.offsetLeft);
    // Sort to get consistent LTR offsets (offsetLeft is always LTR-based)
    const sortedOffsets = [...offsets].sort((a, b) => a - b);
    this.itemOffset = sortedOffsets[1] - sortedOffsets[0];

    this.realCount = this.realItems.length;
    this.clonesBefore = this.realCount;
    this.slidesPerPage = Math.max(1, Math.round(this.slider.clientWidth / this.itemOffset));
    this.realWidth = this.realCount * this.itemOffset;
    this.cloneBeforeWidth = this.clonesBefore * this.itemOffset;
  }

  // Normalize scrollLeft to always be a positive LTR-style value
  _getScrollLeft() {
    const sl = this.slider.scrollLeft;
    if (!this.isRTL) return sl;

    // Firefox RTL: scrollLeft is negative
    if (sl <= 0) {
      return -sl;
    }
    // Safari/Chrome RTL: scrollLeft = maxScroll - position (can be positive)
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    if (sl > 0) {
      return maxScroll - sl;
    }
    return 0;
  }

  // Set scrollLeft from a normalized positive value
  _setScrollLeft(value, behavior = 'auto') {
    let target = value;

    if (this.isRTL) {
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      // Firefox: negative
      if (this.slider.scrollLeft <= 0) {
        target = -value;
      } else {
        // Safari/Chrome: inverted
        target = maxScroll - value;
      }
    }

    if (behavior === 'smooth') {
      this.slider.scrollTo({ left: target, behavior: 'smooth' });
    } else {
      this.slider.style.scrollBehavior = 'auto';
      this.slider.scrollLeft = target;
      // Force reflow
      void this.slider.offsetHeight;
      this.slider.style.scrollBehavior = '';
    }
  }

  _goToRealStart() {
    if (!this.itemOffset) return;
    this._isJumping = true;
    this._setScrollLeft(this.cloneBeforeWidth, 'auto');
    requestAnimationFrame(() => {
      setTimeout(() => { this._isJumping = false; }, 100);
    });
  }

  _onScroll() {
    if (this._isJumping || !this.itemOffset) return;

    clearTimeout(this._scrollTimeout);
    this._scrollTimeout = setTimeout(() => {
      this._checkLoop();
    }, 80);
  }

  _checkLoop() {
    if (!this.itemOffset) return;

    const scrollLeft = this._getScrollLeft();
    const cloneBeforeWidth = this.cloneBeforeWidth;
    const realEnd = cloneBeforeWidth + this.realWidth;

    if (scrollLeft < cloneBeforeWidth - 2) {
      // In leading clones → jump forward
      this._isJumping = true;
      this._setScrollLeft(scrollLeft + this.realWidth, 'auto');
      setTimeout(() => { this._isJumping = false; }, 100);
    } else if (scrollLeft >= realEnd - 2) {
      // In trailing clones → jump backward
      this._isJumping = true;
      this._setScrollLeft(scrollLeft - this.realWidth, 'auto');
      setTimeout(() => { this._isJumping = false; }, 100);
    }
  }

  _onButtonClick(event) {
    event.preventDefault();
    if (!this.itemOffset || this._isScrolling) return;

    const step = parseInt(event.currentTarget.dataset.step || 1, 10);
    const direction = event.currentTarget.name === 'next' ? 1 : -1;

    const currentScroll = this._getScrollLeft();
    const targetScroll = currentScroll + direction * step * this.itemOffset;

    this._isScrolling = true;
    this._setScrollLeft(targetScroll, 'smooth');

    clearTimeout(this._clickTimeout);
    this._clickTimeout = setTimeout(() => {
      this._checkLoop();
      this._isScrolling = false;
    }, 450);
  }
}

customElements.define('custom-slider-component', CustomSliderComponent);


class RecentlyViewedSlider extends HTMLElement {
  constructor() {
    super();
    this._isJumping = false;
    this._isScrolling = false;
    this._scrollTimeout = null;
    this._clickTimeout = null;
  }

  connectedCallback() {
    // Slides are injected dynamically, so wait for them
    this._waitForSlides();
  }

  disconnectedCallback() {
    this._mutationObserver?.disconnect();
    this._resizeObserver?.disconnect();
    if (this._scrollHandler) {
      this.slider?.removeEventListener('scroll', this._scrollHandler);
    }
  }

  _waitForSlides() {
    const slider = this.querySelector('[id^="Slider-"]');
    if (slider && slider.querySelectorAll('[id^="Slide-"]').length >= 2) {
      this.init();
      return;
    }

    this._mutationObserver?.disconnect();
    this._mutationObserver = new MutationObserver(() => {
      const s = this.querySelector('[id^="Slider-"]');
      if (s && s.querySelectorAll('[id^="Slide-"]').length >= 2) {
        this._mutationObserver.disconnect();
        this.init();
      }
    });
    this._mutationObserver.observe(this, { childList: true, subtree: true });
  }

  reinit() {
    this._mutationObserver?.disconnect();
    this.init();
  }

  init() {
    // Clean up old listeners and clones
    if (this._scrollHandler) {
      this.slider?.removeEventListener('scroll', this._scrollHandler);
    }
    this.querySelectorAll('.rv-clone').forEach(el => el.remove());

    this.slider     = this.querySelector('[id^="Slider-"]');
    this.realItems  = Array.from(this.querySelectorAll('[id^="Slide-"]'));
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.prevButton || !this.nextButton || this.realItems.length < 2) return;

    this.isRTL =
      document.documentElement.dir === 'rtl' ||
      document.body.dir === 'rtl' ||
      getComputedStyle(this.slider).direction === 'rtl';

    this._setupClones();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._measure();
        this._goToRealStart();

        this._resizeObserver?.disconnect();
        this._resizeObserver = new ResizeObserver(() => {
          this._measure();
          this._goToRealStart();
        });
        this._resizeObserver.observe(this.slider);
      });
    });

    this._scrollHandler = this._onScroll.bind(this);
    this.slider.addEventListener('scroll', this._scrollHandler, { passive: true });

    this.prevButton.onclick = null;
    this.nextButton.onclick = null;
    this.prevButton.addEventListener('click', this._onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this._onButtonClick.bind(this));

    this.prevButton.removeAttribute('disabled');
    this.nextButton.removeAttribute('disabled');
  }

  _setupClones() {
    const items = this.realItems;

    // Trailing clones
    items.forEach(item => {
      const clone = item.cloneNode(true);
      clone.classList.add('rv-clone');
      clone.removeAttribute('id');
      this.slider.appendChild(clone);
    });

    // Leading clones
    [...items].reverse().forEach(item => {
      const clone = item.cloneNode(true);
      clone.classList.add('rv-clone');
      clone.removeAttribute('id');
      this.slider.prepend(clone);
    });
  }

  _measure() {
    const allSlides = Array.from(this.slider.children).filter(el => el.clientWidth > 0);
    if (allSlides.length < 2) return;

    const offsets = allSlides.map(el => el.offsetLeft);
    const sorted  = [...offsets].sort((a, b) => a - b);
    this.itemOffset = sorted[1] - sorted[0];

    this.realCount      = this.realItems.length;
    this.clonesBefore   = this.realCount;
    this.slidesPerPage  = Math.max(1, Math.round(this.slider.clientWidth / this.itemOffset));
    this.realWidth      = this.realCount * this.itemOffset;
    this.cloneBeforeWidth = this.clonesBefore * this.itemOffset;
  }

  _getScrollLeft() {
    const sl = this.slider.scrollLeft;
    if (!this.isRTL) return sl;
    if (sl <= 0) return -sl;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    return maxScroll - sl;
  }

  _setScrollLeft(value, behavior = 'auto') {
    let target = value;

    if (this.isRTL) {
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      target = this.slider.scrollLeft <= 0 ? -value : maxScroll - value;
    }

    if (behavior === 'smooth') {
      this.slider.scrollTo({ left: target, behavior: 'smooth' });
    } else {
      this.slider.style.scrollBehavior = 'auto';
      this.slider.scrollLeft = target;
      void this.slider.offsetHeight;
      this.slider.style.scrollBehavior = '';
    }
  }

  _goToRealStart() {
    if (!this.itemOffset) return;
    this._isJumping = true;
    this._setScrollLeft(this.cloneBeforeWidth, 'auto');
    requestAnimationFrame(() => {
      setTimeout(() => { this._isJumping = false; }, 100);
    });
  }

  _onScroll() {
    if (this._isJumping || !this.itemOffset) return;
    clearTimeout(this._scrollTimeout);
    this._scrollTimeout = setTimeout(() => this._checkLoop(), 80);
  }

  _checkLoop() {
    if (!this.itemOffset) return;

    const scrollLeft = this._getScrollLeft();
    const realEnd    = this.cloneBeforeWidth + this.realWidth;

    if (scrollLeft < this.cloneBeforeWidth - 2) {
      this._isJumping = true;
      this._setScrollLeft(scrollLeft + this.realWidth, 'auto');
      setTimeout(() => { this._isJumping = false; }, 100);
    } else if (scrollLeft >= realEnd - 2) {
      this._isJumping = true;
      this._setScrollLeft(scrollLeft - this.realWidth, 'auto');
      setTimeout(() => { this._isJumping = false; }, 100);
    }
  }

  _onButtonClick(event) {
    event.preventDefault();
    if (!this.itemOffset || this._isScrolling) return;

    const step = parseInt(event.currentTarget.dataset.step || 1, 10);
    const direction = this.isRTL
      ? (event.currentTarget.name === 'next' ? -1 : 1)
      : (event.currentTarget.name === 'next' ? 1 : -1);

    const targetScroll = this._getScrollLeft() + direction * step * this.itemOffset;

    this._isScrolling = true;
    this._setScrollLeft(targetScroll, 'smooth');

    clearTimeout(this._clickTimeout);
    this._clickTimeout = setTimeout(() => {
      this._checkLoop();
      this._isScrolling = false;
    }, 450);
  }
}

customElements.define('recently-viewed-slider', RecentlyViewedSlider);


class SliderComponentCollectionList extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.isInfinite = this.classList.contains('for-collection-list');
    this.isInfiniteActive = false;

    if (!this.slider || !this.nextButton) return;

    // RTL Support
    this.isRTL = document.documentElement.dir === 'rtl' || 
                 document.body.dir === 'rtl' ||
                 getComputedStyle(this.slider).direction === 'rtl';

    this._isJumping = false;
    this._isScrolling = false;
    this._isButtonScrolling = false; // Flag to prevent loop check during button animation
    this._scrollTimeout = null;
    this._clickTimeout = null;

    this.initPages();
    
    const resizeObserver = new ResizeObserver(this.initPages.bind(this));
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.onScrollHandler.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClickWrapper.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClickWrapper.bind(this));

    this._mql = window.matchMedia('(max-width: 989px)');
    if (this._mql.addEventListener) {
      this._mql.addEventListener('change', () => this.initPages());
    }
  }

  disconnectedCallback() {
    if (this._mql && this._mql.removeEventListener) {
      this._mql.removeEventListener('change', () => this.initPages());
    }
  }

  initPages() {
    // 1. Cleanup previous state
    this.slider.querySelectorAll('.slider__slide--clone').forEach(el => el.remove());
    this.isInfiniteActive = false;
    
    // 2. Calculate Base Metrics
    this.realItems = Array.from(this.querySelectorAll('[id^="Slide-"]'));
    this.sliderItemsToShow = this.realItems.filter((element) => element.clientWidth > 0);
    
    if (this.sliderItemsToShow.length < 2) return;

    // Calculate offset (always positive magnitude)
    const offset1 = this.sliderItemsToShow[1].offsetLeft;
    const offset0 = this.sliderItemsToShow[0].offsetLeft;
    this.sliderItemOffset = Math.abs(offset1 - offset0);
    
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );

    // Default (Desktop/Standard) Logic
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

    // 3. Enable Infinite ONLY on Mobile/Tablet (< 990px) when slider class is present
    const isMobileSlider = window.innerWidth < 990 && this.slider.classList.contains('slider');

    if (this.isInfinite && isMobileSlider && this.realItems.length > this.slidesPerPage) {
      this.isInfiniteActive = true;
      
      this._setupInfiniteClones();
      
      this.realWidth = this.realItems.length * this.sliderItemOffset;
      this.cloneBeforeWidth = this.realItems.length * this.sliderItemOffset;
      this.totalPages = this.realItems.length;

      requestAnimationFrame(() => {
        this._goToInfiniteStart(true);
      });
    } else {
      this.isInfiniteActive = false;
      this.slider.querySelectorAll('.slider__slide--clone').forEach(el => el.remove());
      this.slider.scrollLeft = 0;
    }

    this.update();
  }

  _setupInfiniteClones() {
    const items = this.realItems;
    
    // Append Clones (Trailing)
    items.forEach(item => {
      const clone = item.cloneNode(true);
      clone.classList.add('slider__slide--clone');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id], [name]').forEach(el => {
        if(el.id) el.removeAttribute('id');
        if(el.name) el.removeAttribute('name');
      });
      this.slider.appendChild(clone);
    });

    // Prepend Clones (Leading)
    [...items].reverse().forEach(item => {
      const clone = item.cloneNode(true);
      clone.classList.add('slider__slide--clone');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id], [name]').forEach(el => {
        if(el.id) el.removeAttribute('id');
        if(el.name) el.removeAttribute('name');
      });
      this.slider.prepend(clone);
    });
  }

  // Normalizes scrollLeft: 0 = Visual Start, Max = Visual End
  _getScrollLeft() {
    const sl = this.slider.scrollLeft;
    if (!this.isRTL) return sl;

    if (sl <= 0) return Math.abs(sl);
    
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    return maxScroll - sl;
  }

  _setScrollLeft(value, behavior = 'auto') {
    let target = value;
    
    if (this.isRTL) {
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      if (this.slider.scrollLeft < 0 && maxScroll > 0) { 
         target = -value; 
      } else {
         target = maxScroll - value;
      }
    }

    if (behavior === 'smooth') {
      this.slider.scrollTo({ left: target, behavior: 'smooth' });
    } else {
      this.slider.style.scrollBehavior = 'auto';
      this.slider.scrollLeft = target;
      void this.slider.offsetHeight; 
      this.slider.style.scrollBehavior = '';
    }
  }

  _goToInfiniteStart(forceInstant = false) {
    if (!this.sliderItemOffset) return;
    this._isJumping = true;
    this._setScrollLeft(this.cloneBeforeWidth, 'auto');
    
    const resetJump = () => { this._isJumping = false; };
    if (forceInstant) {
      requestAnimationFrame(resetJump);
    } else {
      setTimeout(resetJump, 50);
    }
  }

  onScrollHandler() {
    this.update();
    
    if (this.isInfiniteActive) {
      // Don't check loop if we are jumping OR if a button is currently animating
      if (this._isJumping || this._isButtonScrolling || !this.sliderItemOffset) return;
      
      clearTimeout(this._scrollTimeout);
      this._scrollTimeout = setTimeout(() => {
        this._checkInfiniteLoop();
      }, 80);
    }
  }

  _checkInfiniteLoop() {
    if (!this.sliderItemOffset) return;

    const scrollLeft = this._getScrollLeft();
    const realEnd = this.cloneBeforeWidth + this.realWidth;

    // If we scrolled into the Leading Clones
    if (scrollLeft < this.cloneBeforeWidth - 5) {
      this._isJumping = true;
      this._setScrollLeft(scrollLeft + this.realWidth, 'auto');
      setTimeout(() => { this._isJumping = false; }, 50);
    } 
    // If we scrolled into the Trailing Clones
    else if (scrollLeft >= realEnd - 5) {
      this._isJumping = true;
      this._setScrollLeft(scrollLeft - this.realWidth, 'auto');
      setTimeout(() => { this._isJumping = false; }, 50);
    }
  }

  onButtonClickWrapper(event) {
    if (this.isInfiniteActive) {
      this._onInfiniteButtonClick(event);
    } else {
      this.onButtonClick(event);
    }
  }

  _onInfiniteButtonClick(event) {
    event.preventDefault();
    if (!this.sliderItemOffset || this._isScrolling) return;

    const step = parseInt(event.currentTarget.dataset.step || 1, 10);
    const direction = event.currentTarget.name === 'next' ? 1 : -1;

    const currentScroll = this._getScrollLeft();
    const targetScroll = currentScroll + direction * step * this.sliderItemOffset;

    // Set flag to prevent onScrollHandler from interfering with the animation
    this._isButtonScrolling = true;
    this._isScrolling = true;

    this._setScrollLeft(targetScroll, 'smooth');

    // Check loop after animation completes (approx 400ms)
    clearTimeout(this._clickTimeout);
    this._clickTimeout = setTimeout(() => {
      this._isButtonScrolling = false; // Allow scroll listener to check loop again
      this._checkInfiniteLoop();
      this._isScrolling = false;
    }, 450);
  }

  resetPages() {
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;

    if (this.isInfiniteActive) {
      const scrollLeft = this._getScrollLeft();
      const relativeScroll = scrollLeft - this.cloneBeforeWidth;
      
      this.currentPage = Math.round(relativeScroll / this.sliderItemOffset) + 1;
      
      if (this.currentPage < 1) this.currentPage = this.totalPages + this.currentPage;
      if (this.currentPage > this.totalPages) this.currentPage = this.currentPage - this.totalPages;

      // Always enable buttons
      this.prevButton.removeAttribute('disabled');
      this.nextButton.removeAttribute('disabled');
    } else {
      // Standard Logic
      this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

      if (this.enableSliderLooping) return;

      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.isInfiniteActive ? this.realItems[this.currentPage - 1] : this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!this.sliderItemOffset || this._isScrolling) return;

    const step = parseInt(event.currentTarget.dataset.step || 1, 10);
    const direction = event.currentTarget.name === 'next' ? 1 : -1;

    const currentScroll = this._getScrollLeft();
    const targetScroll = currentScroll + direction * step * this.sliderItemOffset;

    this._isScrolling = true;
    this._setScrollLeft(targetScroll, 'smooth');

    clearTimeout(this._clickTimeout);
    this._clickTimeout = setTimeout(() => {
      if (this.isInfiniteActive) {
        this._checkInfiniteLoop();
      }
      this._isScrolling = false;
    }, 450);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component-collection-list', SliderComponentCollectionList);
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this._isJumping = false;
    this._isScrolling = false;
    this._scrollTimeout = null;
    this._clickTimeout = null;
    this._autoplayTimer = null;
    this._autoplayDelay = 5000;
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.realItems = Array.from(this.sliderItems);
    this.enableSliderLooping = true;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.isRTL =
      document.documentElement.dir === 'rtl' ||
      document.body.dir === 'rtl' ||
      getComputedStyle(this.slider).direction === 'rtl';

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this._onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this._onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.realItems = Array.from(this.sliderItems);
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    this.prevButton.removeAttribute('disabled');
    this.nextButton.removeAttribute('disabled');
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  _onButtonClick(event) {
    event.preventDefault();
    if (!this.sliderItemOffset || this._isScrolling) return;

    const step = parseInt(event.currentTarget.dataset.step || 1, 10);
    const direction = event.currentTarget.name === 'next' ? 1 : -1;

    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;

    this._isScrolling = true;
    this.setSlidePosition(this.slideScrollPosition);

    clearTimeout(this._clickTimeout);
    this._clickTimeout = setTimeout(() => {
      this._isScrolling = false;
    }, 450);
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);




// class SliderComponent extends HTMLElement {
//   constructor() {
//     super();
//     this.slider = this.querySelector('[id^="Slider-"]');
//     this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
//     this.enableSliderLooping = false;
//     this.currentPageElement = this.querySelector('.slider-counter--current');
//     this.pageTotalElement = this.querySelector('.slider-counter--total');
//     this.prevButton = this.querySelector('button[name="previous"]');
//     this.nextButton = this.querySelector('button[name="next"]');

//     if (!this.slider || !this.nextButton) return;

//     this.initPages();
//     const resizeObserver = new ResizeObserver((entries) => this.initPages());
//     resizeObserver.observe(this.slider);

//     this.slider.addEventListener('scroll', this.update.bind(this));
//     this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
//     this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
//   }

//   initPages() {
//     this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
//     if (this.sliderItemsToShow.length < 2) return;
//     this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
//     this.slidesPerPage = Math.floor(
//       (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
//     );
//     this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
//     this.update();
//   }

//   resetPages() {
//     this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
//     this.initPages();
//   }

//   update() {
//     // Temporarily prevents unneeded updates resulting from variant changes
//     // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
//     if (!this.slider || !this.nextButton) return;

//     const previousPage = this.currentPage;
//     this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

//     if (this.currentPageElement && this.pageTotalElement) {
//       this.currentPageElement.textContent = this.currentPage;
//       this.pageTotalElement.textContent = this.totalPages;
//     }

//     if (this.currentPage != previousPage) {
//       this.dispatchEvent(
//         new CustomEvent('slideChanged', {
//           detail: {
//             currentPage: this.currentPage,
//             currentElement: this.sliderItemsToShow[this.currentPage - 1],
//           },
//         })
//       );
//     }

//     if (this.enableSliderLooping) return;

//     if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
//       this.prevButton.setAttribute('disabled', 'disabled');
//     } else {
//       this.prevButton.removeAttribute('disabled');
//     }

//     if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
//       this.nextButton.setAttribute('disabled', 'disabled');
//     } else {
//       this.nextButton.removeAttribute('disabled');
//     }
//   }

//   isSlideVisible(element, offset = 0) {
//     const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
//     return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
//   }

//   onButtonClick(event) {
//     event.preventDefault();
//     const step = event.currentTarget.dataset.step || 1;
//     this.slideScrollPosition =
//       event.currentTarget.name === 'next'
//         ? this.slider.scrollLeft + step * this.sliderItemOffset
//         : this.slider.scrollLeft - step * this.sliderItemOffset;
//     this.setSlidePosition(this.slideScrollPosition);
//   }

//   setSlidePosition(position) {
//     this.slider.scrollTo({
//       left: position,
//     });
//   }
// }

// customElements.define('slider-component', SliderComponent);


class SlideshowComponent extends SliderComponent {
  constructor() {
    super(); // sets up slider, sliderItems, initPages, scroll + button listeners
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;
    this._isJumping = false;
    this._scrollTimeout = null;
    this._clickTimeout = null;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll('.slider-counter__link')
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener('click', this.linkToSlide.bind(this))
    );

    // Detect RTL
    this.isRTL =
      document.documentElement.dir === 'rtl' ||
      document.body.dir === 'rtl' ||
      getComputedStyle(this.slider).direction === 'rtl';

    // Capture real items before cloning (only those with IDs)
    this.realItems = Array.from(this.slider.querySelectorAll('[id^="Slide-"]'));

    // Insert clones for seamless infinite looping
    this._setupClones();

    // Re-measure and snap to real-start after clones render
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._measure();
        this._goToRealStart();

        this._resizeObserver?.disconnect();
        this._resizeObserver = new ResizeObserver(() => {
          this._measure();
          this._goToRealStart();
        });
        this._resizeObserver.observe(this.slider);
      });
    });

    // Additional scroll listener: checks whether we need to silently jump back
    // (The base class already added one that calls update())
    this._loopScrollHandler = this._onLoopScroll.bind(this);
    this.slider.addEventListener('scroll', this._loopScrollHandler, { passive: true });

    // Ensure nav buttons are always enabled
    this.prevButton.removeAttribute('disabled');
    this.nextButton.removeAttribute('disabled');

    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => { this.announcementBarArrowButtonWasClicked = true; },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  /* ─── Clone setup ──────────────────────────────────────────────── */

  _setupClones() {
    const items = this.realItems;
    // Append trailing clones (after the last real slide)
    items.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.classList.add('slideshow-clone');
      clone.removeAttribute('id');
      this.slider.appendChild(clone);
    });
    // Prepend leading clones (before the first real slide), reversed
    [...items].reverse().forEach((item) => {
      const clone = item.cloneNode(true);
      clone.classList.add('slideshow-clone');
      clone.removeAttribute('id');
      this.slider.prepend(clone);
    });
  }

  /* ─── Measurement ───────────────────────────────────────────────── */

  _measure() {
    const allSlides = Array.from(this.slider.children).filter(
      (el) => el.clientWidth > 0
    );
    if (allSlides.length < 2) return;

    const offsets = allSlides.map((el) => el.offsetLeft);
    const sorted = [...offsets].sort((a, b) => a - b);
    this.sliderItemOffset = sorted[1] - sorted[0];

    this.realCount = this.realItems.length;
    this.clonesBefore = this.realCount;
    this.realWidth = this.realCount * this.sliderItemOffset;
    this.cloneBeforeWidth = this.clonesBefore * this.sliderItemOffset;
    this.totalPages = this.realCount; // used by base counter
  }

  /* ─── RTL-normalised scroll helpers ────────────────────────────── */

  _getScrollLeft() {
    const sl = this.slider.scrollLeft;
    if (!this.isRTL) return sl;
    if (sl <= 0) return -sl; // Firefox RTL (negative)
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    return maxScroll - sl; // Safari/Chrome RTL (inverted positive)
  }

  _setScrollLeft(value, behavior = 'auto') {
    let target = value;
    if (this.isRTL) {
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      target = this.slider.scrollLeft <= 0 ? -value : maxScroll - value;
    }
    if (behavior === 'smooth') {
      this.slider.scrollTo({ left: target, behavior: 'smooth' });
    } else {
      this.slider.style.scrollBehavior = 'auto';
      this.slider.scrollLeft = target;
      void this.slider.offsetHeight; // force reflow
      this.slider.style.scrollBehavior = '';
    }
  }

  /* ─── Positioning ───────────────────────────────────────────────── */

  _goToRealStart() {
    if (!this.sliderItemOffset) return;
    this._isJumping = true;
    this._setScrollLeft(this.cloneBeforeWidth, 'auto');
    requestAnimationFrame(() => {
      setTimeout(() => { this._isJumping = false; }, 100);
    });
    this.currentPage = 1;
  }

  /* ─── Scroll → loop check ───────────────────────────────────────── */

  _onLoopScroll() {
    if (this._isJumping || !this.sliderItemOffset) return;
    clearTimeout(this._scrollTimeout);
    this._scrollTimeout = setTimeout(() => { this._checkLoop(); }, 80);
  }

  _checkLoop() {
    if (!this.sliderItemOffset) return;
    const scrollLeft = this._getScrollLeft();
    const cloneBeforeWidth = this.cloneBeforeWidth;
    const realEnd = cloneBeforeWidth + this.realWidth;

    if (scrollLeft < cloneBeforeWidth - 2) {
      // We're in leading clones → silently jump forward into real content
      this._isJumping = true;
      this._setScrollLeft(scrollLeft + this.realWidth, 'auto');
      setTimeout(() => { this._isJumping = false; }, 100);
    } else if (scrollLeft >= realEnd - 2) {
      // We're in trailing clones → silently jump backward into real content
      this._isJumping = true;
      this._setScrollLeft(scrollLeft - this.realWidth, 'auto');
      setTimeout(() => { this._isJumping = false; }, 100);
    }
  }

  /* ─── Overridden: button click ──────────────────────────────────── */

  onButtonClick(event) {
    event.preventDefault();
    if (!this.sliderItemOffset) return;
    this.wasClicked = true;

    const step = parseInt(event.currentTarget.dataset.step || 1, 10);
    // _getScrollLeft() and _setScrollLeft() already normalise RTL coordinates,
    // so direction is always the same regardless of text direction.
    const direction = event.currentTarget.name === 'next' ? 1 : -1;

    const currentScroll = this._getScrollLeft();
    const targetScroll = currentScroll + direction * step * this.sliderItemOffset;
    this._setScrollLeft(targetScroll, 'smooth');

    // After the smooth scroll completes, check whether to silently loop
    clearTimeout(this._clickTimeout);
    this._clickTimeout = setTimeout(() => { this._checkLoop(); }, 450);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  /* ─── Overridden: scroll update (counter + dots) ───────────────── */

  update() {
    if (!this.slider || !this.nextButton) return;
    if (!this.sliderItemOffset || !this.realCount) return;

    const scrollLeft = this._getScrollLeft();
    const rawIndex = Math.round(
      (scrollLeft - this.cloneBeforeWidth) / this.sliderItemOffset
    );
    const n = this.realCount;
    const normalised = ((rawIndex % n) + n) % n;
    const newPage = normalised + 1;

    const previousPage = this.currentPage;
    this.currentPage = newPage;

    // Update "X / N" counter
    const currentPageEl = this.querySelector('.slider-counter--current');
    const pageTotalEl   = this.querySelector('.slider-counter--total');
    if (currentPageEl && pageTotalEl) {
      currentPageEl.textContent = this.currentPage;
      pageTotalEl.textContent   = n;
    }

    if (this.currentPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.realItems[this.currentPage - 1],
          },
        })
      );
    }

    // Buttons are always enabled (infinite loop)
    this.prevButton.removeAttribute('disabled');
    this.nextButton.removeAttribute('disabled');

    // Update dot / number indicators
    const sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    if (!sliderControlButtons.length) return;
    sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    const activeBtn = sliderControlButtons[this.currentPage - 1];
    if (activeBtn) {
      activeBtn.classList.add('slider-counter__link--active');
      activeBtn.setAttribute('aria-current', 'true');
    }
  }

  /* ─── Overridden: autoplay tick ─────────────────────────────────── */

  autoRotateSlides() {
    if (!this.sliderItemOffset) return;
    const currentScroll  = this._getScrollLeft();
    const targetScroll   = currentScroll + this.sliderItemOffset;
    this._setScrollLeft(targetScroll, 'smooth');

    clearTimeout(this._clickTimeout);
    this._clickTimeout = setTimeout(() => { this._checkLoop(); }, 450);

    this.applyAnimationToAnnouncementBar('next');
  }

  /* ─── Overridden: aria / tab visibility (real items only) ────────── */

  setSlideVisibility(event) {
    if (!this.realItems) return;
    this.realItems.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        linkElements.forEach((btn) => btn.removeAttribute('tabindex'));
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        linkElements.forEach((btn) => btn.setAttribute('tabindex', '-1'));
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  /* ─── Overridden: dot / number link click ───────────────────────── */

  linkToSlide(event) {
    event.preventDefault();
    const index = this.sliderControlLinksArray.indexOf(event.currentTarget);
    if (index < 0 || !this.sliderItemOffset) return;

    const targetScroll = this.cloneBeforeWidth + index * this.sliderItemOffset;
    this._setScrollLeft(targetScroll, 'smooth');

    this.currentPage = index + 1;
    this.update();

    clearTimeout(this._clickTimeout);
    this._clickTimeout = setTimeout(() => { this._checkLoop(); }, 450);
  }

  /* ─── Autoplay plumbing (unchanged) ────────────────────────────── */

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin',   this.focusInHandling.bind(this));
    this.addEventListener('focusout',  this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount  = this.realItems.length;
    const increment   = button === 'next' ? 1 : -1;
    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide    = this.realItems[nextIndex];
    const currentSlide = this.realItems[currentIndex];

    const animationClassIn  = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide  = currentIndex === itemsCount - 1;
    const shouldMoveNext =
      (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

(function () {
  const STOREFRONT_TOKEN = 'dc02ff8c539bbda7005ddaf61f25ec62';
  // const STOREFRONT_TOKEN = 'YOUR_STOREFRONT_TOKEN_HERE';

  /* -----------------------------
     Pickup Availability (HTML reload)
  ------------------------------*/
  function updatePickupAvailability(variantId) {
    const container = document.getElementById('pickup-availability-container');
    if (!container) return Promise.resolve();

    const productUrl = container.dataset.url;
    const sectionId  = container.dataset.sectionId;
    if (!productUrl || !sectionId) return Promise.resolve();

    return fetch(`${productUrl}?variant=${variantId}&section_id=${sectionId}`)
      .then((res) => res.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const newContent = parsed.getElementById('pickup-availability-container');
        if (newContent) container.innerHTML = newContent.innerHTML;
      })
      .catch((err) => console.error('Pickup availability update failed:', err));
  }

  /* -----------------------------
     Fetch Store Availability
  ------------------------------*/
  function fetchStoreQuantities(variantId) {
    const query = `{
      node(id: "gid://shopify/ProductVariant/${variantId}") {
        ... on ProductVariant {
          storeAvailability(first: 20) {
            edges {
              node {
                available
                quantityAvailable
                location {
                  name
                }
              }
            }
          }
        }
      }
    }`;

    return fetch('/api/2024-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query })
    })
      .then((res) => res.json())
      .catch((err) => {
        console.error('Storefront API fetch failed:', err);
        return null;
      });
  }

  /* -----------------------------
     Inject Store Pickup Status
  ------------------------------*/
  function injectQuantities(data) {
    const edges = data?.data?.node?.storeAvailability?.edges;
    if (!edges) return;

    edges.forEach(({ node }) => {
      const locationName = node.location.name;
      const qty = node.quantityAvailable;

      const li = document.querySelector(
        `#pickup-availability-container li[data-store-name="${CSS.escape(locationName)}"]`
      );
      if (!li) return;

      const qtySpan = li.querySelector('.store-qty');
      if (!qtySpan) return;

      if (node.available && qty > 1) {
        qtySpan.textContent = 'במלאי';
        qtySpan.style.color = 'green';
      } else if (node.available && qty === 1) {
        qtySpan.textContent = 'במלאי - יחידה אחרונה';
        qtySpan.style.color = 'orange';
      } else {
        qtySpan.textContent = 'אזל המלאי';
        qtySpan.style.color = 'red';
      }
    });
  }

  /* -----------------------------
     Shipping Status (GLOBAL)
     ✅ Uses Shopify Liquid (includes warehouse)
  ------------------------------*/
  function updateShippingStatus(available) {
    const el = document.getElementById('shipping-stock-status');
    if (!el) return;

    if (available) {
      el.textContent = 'במלאי - זמין למשלוח';
      el.style.color = 'green';
    } else {
      el.textContent = 'אזל המלאי';
      el.style.color = 'red';
    }
  }

  /* -----------------------------
     Get variant availability (Liquid JSON)
  ------------------------------*/
  function getVariantAvailability(variantId) {
    const variantData = window.productVariants?.find(v => v.id == variantId);
    return variantData ? variantData.available : false;
  }

  /* -----------------------------
     MAIN HANDLER
  ------------------------------*/
  async function handleVariantChange(variantId) {
    if (!variantId) return;

    // Update pickup UI
    await updatePickupAvailability(variantId);

    // Fetch store data
    const data = await fetchStoreQuantities(variantId);
    if (data) injectQuantities(data);

    // Update shipping (GLOBAL)
    const isAvailable = getVariantAvailability(variantId);
    updateShippingStatus(isAvailable);
  }

  /* -----------------------------
     Variant Change Listener
  ------------------------------*/
  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const variantId = event?.data?.variant?.id;
    handleVariantChange(variantId);
  });

  /* -----------------------------
     Initial Load
  ------------------------------*/
  document.addEventListener('DOMContentLoaded', () => {
    const variantInput = document.querySelector(
      'input[name="id"]:checked, select[name="id"], input[name="id"]'
    );

    if (!variantInput) return;

    const variantId = variantInput.value;
    handleVariantChange(variantId);
  });

})();